import { paginatePrintSheet } from './paginatePrintSheet';

// Printing in a clean window.
//
// The prescription slip is laid out entirely with inline styles on ONE root element: its width,
// its A4 min-height, its page padding, and the flex column that pins the footer to the bottom of
// the sheet. Both print buttons used to copy `node.innerHTML`, which takes the children and throws
// that root away. Every one of those rules went with it, so the printed page had no page margin
// (text ran into the paper edge and was clipped), no column layout (the footer floated up under
// the content, leaving half the sheet blank), and no base font size. The component's own
// `@media print` block survived the copy but targeted `.prescription-print`, a class that no
// longer existed in the print document, so it did nothing at all.
//
// Copying `outerHTML` keeps the root and everything on it. That single difference is what makes
// the printed sheet match the preview.
//
// The window is also a fresh document with none of the app's stylesheets, so it has to load the
// Urdu face itself. The app gets Noto Nastaliq Urdu from Google Fonts (App.css); this window never
// did, and on a PC with no Urdu font installed every line of Urdu on the paper came out in Arial
// while the preview on screen showed Nastaliq.
//
// Three documents print through here: the A4 prescription (printNode), the 80mm token slip
// (TokenPrintModal) and the 80mm invoice receipt (InvoiceReceipt). All go through printHtml, so all
// get the Urdu face and the wait for it. The token slip used to build a window of its own, and
// printed its Urdu note in Courier New.

/** The face every line of Urdu on a printed sheet is set in, then what to fall back to. */
export const URDU_PRINT_FONT = "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', sans-serif";

/**
 * Goes inside an `@page` rule that leaves a page margin. Chrome prints its own header (the date
 * and the page's title) and footer (the page's address, "about:blank" for a print window, and
 * "1/2") in that margin unless "Headers and footers" is unticked in the print dialog, every time.
 * A page that puts a box of its own in the top margin loses the header, and one in the bottom
 * margin loses the footer: each edge only its own (Chrome 131 on, checked on 155). So the top gets
 * an empty box, and the bottom numbers the pages, the one thing the footer was doing for us; a
 * sheet that is never more than a page (`numbered: false`) gets an empty box there too.
 */
export const pageMarginBoxes = ({ numbered = true } = {}) => `@top-center { content: ""; } ${numbered
  ? '@bottom-right { content: "Page " counter(page) " of " counter(pages); font-family: Arial, sans-serif; font-size: 9px; color: #6b7280; }'
  : '@bottom-center { content: ""; }'}`;

// The same family and weights App.css imports, so the font files are ones the app has already
// downloaded and this window takes them from the cache.
export const URDU_FONT_CSS =
  'https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;500;600;700&display=swap';

// The prescription sheet's page rules.
export const PRESCRIPTION_STYLES = `
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; }
      body { font-family: Arial, sans-serif; background: #fff; }

      /* The sheet is A4 and carries its own page padding, so the page box itself takes none.
         Chrome's "Margins: Default" would otherwise add a second margin on top of the padding
         and squeeze the layout inward. */
      @page { size: A4; margin: 0; }

      @media print {
        body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        /* Nothing may hang outside the sheet: an overflowing child would be silently clipped at
           the paper edge, which is exactly how the Urdu column used to lose its last words. */
        .prescription-print { overflow: hidden; }
      }`;

/**
 * Opens a print window holding `bodyHtml` under `styles`, and prints it.
 *
 * Waits for fonts and images before printing rather than guessing with a fixed delay: the clinic
 * logo is fetched from the API and the Urdu face is a web font, and printing before either
 * settled produced a slip with a missing logo or Latin fallback text where the Urdu should be.
 *
 * @param {object}     opts
 * @param {string}     opts.title          document title (shows in the print dialog and any PDF filename)
 * @param {string}     opts.styles         the document's own CSS
 * @param {string}     opts.bodyHtml       the markup to print
 * @param {string}     [opts.features]     window.open features, e.g. a small window for a receipt
 * @param {(win: Window) => void} [opts.beforePrint] run once fonts and images have settled and
 *   before the dialog opens, with the print window's own document laid out. Anything that has to
 *   MEASURE the page belongs here: earlier and the Urdu face and the logo are still missing, so
 *   every height is wrong.
 * @param {() => void} [opts.onAfterPrint] run once the dialog has been dismissed
 * @returns {boolean} false when the browser blocked the popup, so the caller can say so
 */
export function printHtml({ title, styles = '', bodyHtml = '', features, beforePrint, onAfterPrint } = {}) {
  const win = features ? window.open('', '_blank', features) : window.open('', '_blank');
  if (!win) return false; // popup blocked — nothing to clean up

  win.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title || 'Print')}</title>
    <link rel="stylesheet" href="${URDU_FONT_CSS}" />
    <style>
      ${styles}

      @font-face {
        font-family: 'Jameel Noori Nastaleeq';
        src: local('Jameel Noori Nastaleeq'), local('Noto Nastaliq Urdu');
      }
    </style>
  </head>
  <body>${bodyHtml}</body>
</html>`);
  win.document.close();
  win.focus();

  const fire = once(() => {
    try {
      if (beforePrint) beforePrint(win);
    } catch (err) {
      // A sheet that could not be paginated still prints, as the one long sheet it was before.
      // Losing the letterhead on page 2 is better than losing the prescription.
      if (typeof console !== 'undefined') console.error('Print preparation failed', err);
    }
    try {
      win.print();
      win.close();
    } catch {
      /* window already gone */
    }
    if (onAfterPrint) onAfterPrint();
  });

  // Whichever finishes first: everything loaded, or a ceiling so a slow asset can never leave the
  // user staring at a blank window with no dialog.
  Promise.race([whenReady(win), delay(3000)]).then(fire);
  return true;
}

/**
 * Opens a print window containing the prescription `node` exactly as rendered, and prints it.
 *
 * @param {HTMLElement} node    the element to print, styles and all
 * @param {string}      title   document title (shows in the print dialog and any PDF filename)
 * @param {object}      [opts]
 * @param {() => void}  [opts.onAfterPrint] run once the dialog has been dismissed
 */
export function printNode(node, title, { onAfterPrint } = {}) {
  if (!node) return;
  printHtml({
    title,
    styles: PRESCRIPTION_STYLES,
    bodyHtml: node.outerHTML,
    // Cut the sheet into pages once it is laid out in the print window, so each one carries the
    // letterhead, the patient bar and the footer, and says which page it is. See
    // utils/paginatePrintSheet.js for why CSS alone cannot do this.
    beforePrint: (win) => {
      const sheet = win.document.querySelector('.prescription-print');
      if (sheet) paginatePrintSheet(sheet);
    },
    onAfterPrint,
  });
}

/** Settles once `win`'s stylesheets, fonts and pictures have loaded or failed. */
export function whenReady(win) {
  const doc = win.document;
  // The Urdu face cannot start loading until its stylesheet has arrived: before that the browser
  // does not know the face exists, and fonts.ready can settle with the Urdu still set in Arial.
  // A stylesheet that fails (no internet) settles too, and the sheet prints in the fallback face.
  const links = doc.querySelectorAll ? Array.from(doc.querySelectorAll('link[rel="stylesheet"]')) : [];
  const sheets = links.map((link) =>
    link.sheet
      ? Promise.resolve()
      : new Promise((resolve) => {
          link.addEventListener('load', resolve, { once: true });
          link.addEventListener('error', resolve, { once: true });
        })
  );
  const fonts = Promise.all(sheets).then(() => {
    if (!doc.fonts) return undefined;
    // Laying the page out makes it ask for every face it uses, at whatever weight (the token slip's
    // Urdu is bolder than the prescription's), and fonts.ready then waits on all of them. The
    // regular Urdu face is also asked for by name, in case layout has not reached it.
    if (doc.body && doc.body.getBoundingClientRect) doc.body.getBoundingClientRect();
    const urdu = doc.fonts.load
      ? doc.fonts.load('16px "Noto Nastaliq Urdu"', 'اردو').catch(() => {})
      : undefined;
    return Promise.resolve(urdu).then(() => doc.fonts.ready);
  });
  const images = Array.from(doc.images).map((img) =>
    img.complete
      ? Promise.resolve()
      : new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true }); // a broken logo still prints
        })
  );
  return Promise.all([fonts, ...images]);
}

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function once(fn) {
  let called = false;
  return (...args) => {
    if (called) return;
    called = true;
    return fn(...args);
  };
}

export function escapeHtml(s) {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}
